import { Avatar, Box, Button, Typography, Chip } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Magnifer from "../../Assets/Icons/Magnifer.svg";
import useDebounce from "../../Components/useDebounce";
import DataGridTable from "../../Components/DataGridTable";
import AxiosInstance from "../../Config/AxiosInstance";
import { ApiList } from "../../Config/ApiList";
import AddClubModal from "../../Components/AddClubModal";
import CenterCard from "../../Components/CenterCard";
import PathList from "../../Common/PathList";

const ManageClub = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState([10]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [club, setClub] = useState({ r: [], count: 0 });
  const debouncedSearch = useDebounce(search, 500);
  const UserRows = useMemo(() => {
    return club?.r?.map((x, i) => ({
      rowId: i + 1 + page * rowsPerPage,
      ...x,
    }));
  }, [club?.r, page, rowsPerPage]);

  const GetAllClubs = async () => {
    setIsLoading(true);
    try {
      const params = {
        skip: page * rowsPerPage,
        limit: rowsPerPage,
        search: debouncedSearch,
        order_by: "id",
        order: "desc",
      };
      const response = await AxiosInstance?.get(ApiList?.GET_ALL_CLUBS, {
        params: {
          ...params,
        },
      });
      if (response?.data?.s === 1) {
        setClub(response?.data);
        setTotalCount(response?.data?.t);
        setIsLoading(false);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  };
  useEffect(() => {
    GetAllClubs();
  }, [debouncedSearch, page, rowsPerPage]);
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const userColumns = [
    {
      field: "rowId",
      headerName: "Sr.",
      width: 70,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "logo",
      headerName: "Logo",
      width: 130,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        return params?.row?.logo ? (
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              cursor: "pointer",
            }}
          >
            <CenterCard

              cover_img={params?.row?.logo}
              sx={{
                width: "40px",
                height: "40px",
                objectFit: "cover",
                boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
                borderRadius: "12px",
                transition: "box-shadow 0.2s ease-in-out",
                "&:hover": {
                  boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                },
              }}
              style={{ objectFit: "cover", borderRadius: "12px" }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              cursor: "pointer",
            }}
          >
            <Avatar />
          </Box>
        );
      },
    },
    {
      field: "club_name",
      headerName: "Club Name",
      minWidth: 200,
      headerAlign: "center",
      flex: 1,
      align: "center",
    },
    {
      field: "club_code",
      headerName: "Club ID",
      minWidth: 200,
      headerAlign: "center",
      flex: 1,
      align: "center",
    },
    {
      field: "location",
      headerName: "Location",
      minWidth: 200,
      headerAlign: "center",
      flex: 1,
      align: "center",
      renderCell: (params) => {
        return params?.row?.location ? `${params?.row?.location}` : "-";
      },
    },
    {
      field: "rds_status",
      headerName: "Status",
      minWidth: 150,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const status = params?.row?.rds_status || "inactive";
        return (
          <Chip
            label={status}
            color={status === "active" ? "success" : "default"}
            size="small"
            sx={{ textTransform: "capitalize" }}
          />
        );
      },
    },
    {
      field: "phone_number",
      headerName: "Contact",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      flex: 1,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{ fontSize: "12px", fontWeight: "400", color: "#000" }}
            >
              {" "}
              {params?.row?.phno
                ? `+${params?.row?.phno_cc} ${params?.row?.phno}`
                : "-"}
            </Typography>
            <Typography
              sx={{ fontSize: "12px", fontWeight: "400", color: "#000" }}
            >
              {" "}
              {params?.row?.email ? params?.row?.email : "-"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "Action",
      headerName: "Action",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const isActive = params?.row?.rds_status === "active";

        return (
          <Box
            sx={{
              display: "flex",
              gap: "10px",
              alignItems: "center",
              width: "100%",
              height: "100%",
              justifyContent: "center",
            }}
          >
            <Button
              disabled={!isActive}
              onClick={() => {
                if (isActive) {
                  navigate(`/club-details/${params?.row?.id}`);
                }
              }}
              sx={{
                background: isActive ? "#00D8A2" : "#00000030",
                color: isActive ? "#000938" : "#666",
                borderRadius: "15px",
                fontWeight: "600",
                textTransform: "none",
                "&:hover": {
                  background: isActive ? "#00C294" : "#00000030",
                },
                "&:disabled": {
                  color: "#666",
                  cursor: "not-allowed",
                },
              }}
            >
              View Club
            </Button>
          </Box>
        );
      },
    },
  ];
  return (
    <>
      <Box sx={{ width: "100%", height: "100vh", overflow: "scroll" }}>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 1,
            justifyContent: "space-between",
            p: 3,
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "24px", lg: "26px" },
              fontWeight: "600",
              color: "#000938",
            }}
          >
            Club Management
          </Typography>
          <Box sx={{ display: "flex" }}>
            <Box
              sx={{
                width: { xs: "80%", md: "450px" },
                position: "relative",
                display: "flex",
                justifyContent: "space-between",
                gap: 3,
              }}
            >
              <img
                src={Magnifer}
                alt=""
                style={{ position: "absolute", top: "10px", left: "15px" }}
              />
              <input
                type="text"
                placeholder="Search"
                value={search?.trimStart()}
                onChange={(e) => {
                  const value = e.target.value?.trimStart();
                  setSearch(value);
                }}
                style={{
                  border: "none",
                  borderRadius: "15px",
                  background: "#00000020",
                  padding: "10px 10px 10px 45px",
                  outline: "none",
                  width: "100%",
                }}
              />
              <Button
                sx={{
                  width: "150px",
                  background: "#00D8A2",
                  color: "#000938",
                  borderRadius: "15px",
                  fontWeight: "600",
                  textTransform: "none",
                }}
                onClick={handleOpenModal}
              >
                + Add Club
              </Button>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            width: "100%",
            height: { xs: "auto", lg: "calc(100vh - 145px)" },
            p: { xs: 1, md: 3 },
            pb: 0,
          }}
        >
          <DataGridTable
            height={"100%"}
            columns={userColumns}
            isLoading={isLoading}
            totalcount={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            rows={UserRows}
            handleChangePage={(event, newPage) => {
              setPage(newPage);
            }}
            handleChangeRowsPerPage={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </Box>
      </Box>

      <AddClubModal
        open={isModalOpen}
        onClose={handleCloseModal}
        GetAllClubs={GetAllClubs}
      />
    </>
  );
};

export default ManageClub;
